import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';

// Helper component for displaying a profile detail
const DetailItem = ({ label, value }) => (
    <div>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</h4>
        <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value || 'Not provided'}</p>
    </div>
);

export default function RecipientProfilePage() {
  const { user, token } = useAuth(); // Get basic user info and auth token
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const location = useLocation();
  const isSidebarOpen = location?.state?.isSidebarOpen;

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/recipient`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch profile data.');
        }
        const data = await response.json();
        setProfileData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  if (loading) {
    return <div className="text-center p-10">Loading profile...</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-500">{error}</div>;
  }

  // Combine basic user info with detailed profile data
  const displayName = `${profileData?.first_name || user?.first_name} ${profileData?.last_name || ''}`.trim();
  const displayEmail = profileData?.email || user?.email;
  const profileImageUrl = profileData?.profile_photo_url || `https://ui-avatars.com/api/?name=${displayName}&background=e46809&color=fff`;

  return (
    <div className={`max-w-4xl mx-auto ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`} >
      <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">My Profile</h1>
      <p className="mt-1 text-gray-500 dark:text-gray-400">This is the information you provided during onboarding.</p>

      <div className="mt-8 bg-white dark:bg-gray-800 shadow-lg rounded-xl overflow-hidden">
        <div className="p-8 flex flex-col sm:flex-row items-center gap-8 border-b border-gray-200 dark:border-gray-700">
          <img 
            src={profileImageUrl}
            alt="Profile"
            className="w-32 h-32 rounded-full object-cover border-4 border-orange-500" 
          />
          <div className="text-center sm:text-left">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">{displayName}</h2>
            <p className="text-md text-gray-500 dark:text-gray-400">{displayEmail}</p>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Personal Details</h3>
            <div className="space-y-4">
              <DetailItem label="Age" value={profileData?.age} />
              <DetailItem label="Gender" value={profileData?.gender} />
              <DetailItem label="State of Origin" value={profileData?.state_of_origin} />
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-4">Verification Document</h3>
            <div className="space-y-4">
              <DetailItem label="ID Type" value={profileData?.id_type} />
              <div>
                <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">Uploaded Document</h4>
                {profileData?.id_document_url ? (
                  <a 
                    href={profileData.id_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-lg font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 underline"
                  >
                    View Document
                  </a>
                ) : (
                  <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Not provided</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}